/*
 * Onboarding step configuration.
 * Each step is checked in order; the first one whose `isComplete` returns false
 * becomes the active step. When all are complete, onboarding hides.
 *
 * Completion sources (server-first, so the modal doesn't reappear when a user
 * signs in on a new device/browser where localStorage is empty):
 *  - Server-derived signal from `currentUser` (followingCount, username/fullName,
 *    creator flag) — the source of truth across devices.
 *  - Existing creator `banner_state` for opt-out on step 3 (server-persisted).
 *  - localStorage flag as a per-device fallback (marks steps user actively
 *    skipped/completed in the modal so they don't reappear this session).
 */

const key = (userId, id) => `onboarding.${userId}.${id}.done`;

export const isStepMarkedDone = (userId, id) =>
  typeof window !== 'undefined' && window.localStorage.getItem(key(userId, id)) === '1';

export const markStepDone = (userId, id) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(key(userId, id), '1');
};

export const clearStep = (userId, id) => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(key(userId, id));
};

// The CurrentUser API returns `creator` (Jackson serializes Java boolean
// fields without the `is` prefix). Some callers still read `isCreator`, so
// accept either shape defensively.
const isUserCreator = (user) => Boolean(user?.creator || user?.isCreator);

// A user has "set their profile" once both display name and username exist
// on the server. These are collected at signup, so returning users are
// considered done — the modal is meant to nudge net-new users, not re-prompt
// existing ones every time they sign in on a fresh device.
const hasSetProfile = (user) =>
  Boolean(user?.fullName && String(user.fullName).trim()) &&
  Boolean(user?.username && String(user.username).trim());

const hasFollowedCreators = (user) => Number(user?.followingCount || 0) > 0;

export const ONBOARDING_STEPS = [
  {
    id: 'follow-creators',
    order: 1,
    title: 'Follow creators you like',
    subtitle: "We'll personalize your feed based on who you follow.",
    ctaText: 'Find creators',
    action: 'open-suggested-modal',
    isComplete: (user) =>
      hasFollowedCreators(user) || isStepMarkedDone(user.id, 'follow-creators'),
  },
  {
    id: 'set-profile',
    order: 2,
    title: 'Add your name & username',
    subtitle: 'Make your profile yours so people can find and remember you.',
    ctaText: 'Set it up',
    action: 'open-profile-modal',
    isComplete: (user) => hasSetProfile(user) || isStepMarkedDone(user.id, 'set-profile'),
  },
  {
    id: 'become-creator',
    order: 3,
    title: 'Become a Creator',
    subtitle: 'Share your passion, build your audience, and start earning.',
    ctaText: 'Get Started',
    action: 'navigate-creator-settings',
    // The API returns the status in lowercase ("opted_out"); compare without caring about case.
    isComplete: (user, bannerState) =>
      isUserCreator(user) ||
      String(bannerState?.status || '').toUpperCase() === 'OPTED_OUT' ||
      isStepMarkedDone(user.id, 'become-creator'),
  },
];

export const TOTAL_STEPS = ONBOARDING_STEPS.length;
